import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  CircularProgress
} from '@mui/material';
import { 
  PersonAdd, 
  People, 
  Payment, 
  ExitToApp
} from '@mui/icons-material';
import api from '../services/api';
import { userService } from '../services/userService';

// Importar AuthContext
import { useAuth } from '../auth/AuthContext';

// Importar componentes
import EmployeeForm from '../components/EmployeeForm';
import EmployeeList from '../components/EmployeeList';
import PayrollProcessor from '../components/nominas/PayrollProcessor';
import AbsenceRegister from '../components/AbsenceRegister';
import EmployeeEditDialog from '../components/EmployeeEditDialog';

// Crear un componente de icono bicolor personalizado
const BicolorEventIcon = () => (
  <Box sx={{ position: 'relative', width: 50, height: 50 }}>
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '50%',
        backgroundColor: '#d32f2f', // Rojo para faltas injustificadas
        borderTopLeftRadius: '50%',
        borderTopRightRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>!</span>
    </Box>
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '50%',
        backgroundColor: '#2e7d32', // Verde para faltas justificadas
        borderBottomLeftRadius: '50%',
        borderBottomRightRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
    </Box>
  </Box>
);

const Dashboard = () => {
  // Usar AuthContext
  const { user, logout } = useAuth();
  
  const [employees, setEmployees] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalEmployees: 0,
    totalPayrolls: 0
  });
  const [empresaId, setEmpresaId] = useState(null);
  const [loadingEmpresa, setLoadingEmpresa] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Estados para controlar la apertura de los diálogos
  const [employeeFormOpen, setEmployeeFormOpen] = useState(false);
  const [employeeListOpen, setEmployeeListOpen] = useState(false);
  const [payrollProcessorOpen, setPayrollProcessorOpen] = useState(false);
  const [absenceRegisterOpen, setAbsenceRegisterOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Obtener nombre de la empresa desde el contexto de autenticación
  const empresaNombre = user?.empresa_nombre || `Empresa ${user?.empresa_id || ''}`;

  // Función para manejar logout usando AuthContext
  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Función para obtener headers con autenticación
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  // Función para obtener empleados (optimizada y simplificada)
  const fetchEmployees = useCallback(async () => {
    if (!empresaId) {
      return;
    }

    try {
      const response = await api.get('/empleados/', {
        params: { empresa: empresaId }
      });
      
      if (response.status === 200) {
        setEmployees(response.data);
        setSummaryData(prev => ({
          ...prev,
          totalEmployees: response.data.length
        }));
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      // EL INTERCEPTOR SE ENCARGARÁ AUTOMÁTICAMENTE DE LOS ERRORES 401
    }
  }, [empresaId]); // Solo depende de empresaId

  // Función para obtener datos de resumen
  const fetchSummaryData = useCallback(async () => {
    try {
      setSummaryData(prev => ({
        ...prev,
        totalPayrolls: 0
      }));
    } catch (error) {
      console.error('Error fetching summary data:', error);
    }
  }, []);

  // Cargar información de la empresa (OPTIMIZADO)
  useEffect(() => {
    const loadEmpresaData = async () => {
      try {
        setLoadingEmpresa(true);
        
        // Resetear contadores al cargar
        userService.resetVerificationCount();
        
        // Obtener empresa_id del usuario autenticado
        let id = user?.empresa_id;
        
        // Si no está en el usuario, intentar obtenerlo del userService
        if (!id) {
          id = await Promise.race([
            userService.getEmpresaId(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Timeout obteniendo empresa ID')), 5000)
            )
          ]);
        }
        
        setEmpresaId(id);
        
      } catch (error) {
        console.error('Error loading empresa data:', error);
        // Usar valor por defecto para evitar bucles
        const defaultId = 34;
        setEmpresaId(defaultId);
      } finally {
        setLoadingEmpresa(false);
        setInitialLoadComplete(true);
      }
    };
    
    loadEmpresaData();
  }, [user]);

  // Efecto para cargar datos iniciales (OPTIMIZADO)
  useEffect(() => {
    if (empresaId && initialLoadComplete) {
      // Usar timeout para evitar llamadas inmediatas
      const timer = setTimeout(() => {
        fetchEmployees();
        fetchSummaryData();
      }, 1000);
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [empresaId, initialLoadComplete, fetchEmployees, fetchSummaryData]);

  // Función para mostrar errores
  const showError = (message) => {
    alert(`❌ Error: ${message}`);
  };

  // Función para mostrar éxito
  const showSuccess = (message) => {
    alert(`✅ Éxito: ${message}`);
  };

  // Función para guardar nuevo empleado (MEJORADA)
  const handleSaveEmployee = async (employeeData) => {
    if (!empresaId) {
      alert('Error: No se pudo identificar la empresa');
      return;
    }

    try {
      const datosParaEnviar = {
        ...employeeData,
        empresa: empresaId
      };
      
      const response = await api.post('/empleados/', datosParaEnviar, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 201) {
        alert('Empleado creado exitosamente');
        setEmployeeFormOpen(false);
        await fetchEmployees();
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      
      // Manejo específico de errores de RFC
      if (error.response?.data) {
        if (error.response.data.rfc) {
          // Error específico de RFC
          const rfcError = Array.isArray(error.response.data.rfc) 
            ? error.response.data.rfc.join(', ')
            : error.response.data.rfc;
          
          alert(`❌ Error en el RFC:\n${rfcError}\n\nPor favor verifica que el RFC tenga el formato correcto para persona física.`);
        } 
        else if (error.response.data.nss) {
          // Error específico de NSS
          const nssError = Array.isArray(error.response.data.nss) 
            ? error.response.data.nss.join(', ')
            : error.response.data.nss;
          
          alert(`❌ Error en el NSS:\n${nssError}`);
        }
        else if (error.response.data.non_field_errors) {
          // Errores generales
          const generalError = Array.isArray(error.response.data.non_field_errors) 
            ? error.response.data.non_field_errors.join(', ')
            : error.response.data.non_field_errors;
          
          alert(`❌ Error:\n${generalError}`);
        }
        else if (typeof error.response.data === 'object') {
          // Otros errores de validación
          const errors = Object.entries(error.response.data)
            .map(([key, value]) => {
              const fieldName = key.replace(/_/g, ' ');
              return `${fieldName}: ${Array.isArray(value) ? value.join(', ') : value}`;
            })
            .join('\n');
          alert(`❌ Errores de validación:\n${errors}`);
        } else {
          alert(`❌ Error: ${String(error.response.data)}`);
        }
      } else if (error.message) {
        alert(`❌ Error: ${error.message}`);
      } else {
        alert('❌ Error desconocido al crear empleado');
      }
    }
  };

  // Función para editar empleado (optimizada) - CORREGIDA
  const handleEditEmployee = async (employeeId, employeeData) => {
    try {
      // Procesar datos para el backend
      const processedData = {
        ...employeeData,
        dias_descanso: Array.isArray(employeeData.dias_descanso) 
          ? employeeData.dias_descanso.map(dia => parseInt(dia))
          : [],
        empresa: empresaId // ← AÑADIR ESTA LÍNEA
      };
      
      // Convertir campos numéricos
      if (processedData.sueldo_mensual) {
        processedData.sueldo_mensual = parseFloat(processedData.sueldo_mensual);
      }
      if (processedData.salario_diario) {
        processedData.salario_diario = parseFloat(processedData.salario_diario);
      }
      
      // Limpiar campos según el periodo nominal
      if (processedData.periodo_nominal === 'MENSUAL') {
        processedData.salario_diario = null;
      } else {
        processedData.sueldo_mensual = null;
      }
      
      // Remover campos que no deberían enviarse (pero mantener empresa)
      const fieldsToRemove = [
        'id', 'empresa_nombre', 'activo', 
        'fecha_baja', 'motivo_baja', 'created_at', 
        'updated_at', 'nombre_completo'
      ];
      
      fieldsToRemove.forEach(field => {
        if (field in processedData) {
          delete processedData[field];
        }
      });
      
      const response = await api.put(`/empleados/${employeeId}/`, processedData, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 200) {
        showSuccess('Empleado actualizado exitosamente');
        setEditDialogOpen(false);
        setEditingEmployee(null);
        await fetchEmployees();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating employee:', error);
      
      let errorMessage = 'Error al actualizar empleado';
      
      if (error.response?.data) {
        if (error.response.data.detalles) {
          const errors = Object.entries(error.response.data.detalles)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMessage = `Errores de validación:\n${errors}`;
        } else if (typeof error.response.data === 'object') {
          const errors = Object.entries(error.response.data)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMessage = `Error del servidor:\n${errors}`;
        } else {
          errorMessage = String(error.response.data);
        }
      } else {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
      return false;
    }
  };

  // Función para eliminar empleado
  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este empleado?')) {
      try {
        const response = await api.delete(`/empleados/${employeeId}/`, {
          headers: getAuthHeaders()
        });
        
        if (response.status === 204) {
          showSuccess('Empleado eliminado exitosamente');
          fetchEmployees();
        }
      } catch (error) {
        console.error('Error deleting employee:', error);
        showError('Error al eliminar empleado: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  // Función para procesar nómina
  const handleProcessPayroll = async (payrollData) => {
    try {
      const response = await api.post('/nominas/procesar_nomina/', payrollData, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 200 || response.status === 201) {
        showSuccess('Nómina procesada exitosamente');
        setPayrollProcessorOpen(false);
        fetchSummaryData();
      }
    } catch (error) {
      console.error('Error processing payroll:', error);
      showError('Error al procesar nómina: ' + (error.response?.data?.message || error.message));
      return false;
    }
    return true;
  };

  // Función para registrar faltas (ACTUALIZADA para soportar múltiples empleados)
  const handleRegisterAbsences = async (selectedEmployees, resultData) => {
    try {
      // Si el registro fue exitoso (según el resultado del componente AbsenceRegister)
      if (resultData && resultData.success) {
        // Mostrar mensaje de éxito con los detalles
        showSuccess(`${resultData.message}\n\n` +
              `Motivo: ${resultData.detalle?.motivo || 'N/A'}\n` +
              `Fechas: ${resultData.detalle?.fechas || 'N/A'}\n` +
              `Empleados afectados: ${resultData.detalle?.empleados_afectados || resultData.empleados_afectados || 'N/A'}\n` +
              `Tipo: ${resultData.detalle?.tipo || resultData.tipo || 'N/A'}`);
        
        // Actualizar lista de empleados
        await fetchEmployees();
        return true;
      } else if (resultData && !resultData.success) {
        // Mostrar error si el registro falló
        showError(`${resultData.message || 'No se pudieron registrar las faltas'}`);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error handling absences registration:', error);
      
      // Mostrar error genérico
      showError('Error al procesar el registro de faltas');
      return false;
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {empresaNombre ? `Gestor de Nóminas de ${empresaNombre}` : 'Gestor de Nóminas'}
            {user && ` - Usuario: ${user.username || user.email}`}
          </Typography>
          <Button 
            color="inherit" 
            onClick={handleLogout}
            startIcon={<ExitToApp />}
            sx={{ textTransform: 'none' }}
          >
            Cerrar Sesión
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {loadingEmpresa ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Cargando información de la empresa...</Typography>
          </Box>
        ) : (
          <>
            {/* Títulos centrados según lo solicitado */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                {empresaNombre ? `Gestor de Nóminas de ${empresaNombre}` : 'Gestor de Nóminas'}
              </Typography>
              <Typography variant="h5" component="h2" sx={{ color: '#7f8c8d', fontWeight: 'normal' }}>
                Panel de Control
              </Typography>
            </Box>
            
            <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid item xs={12} sm={6} md={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    color: 'white',
                    borderRadius: 3
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                    <People sx={{ fontSize: 40, mb: 1 }} />
                    <Typography gutterBottom variant="h5" component="h2">
                      Total de Empleados
                    </Typography>
                    <Typography variant="h3" component="p">
                      {summaryData.totalEmployees}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    background: 'linear-gradient(45deg, #66BB6A 30%, #81C784 90%)',
                    color: 'white',
                    borderRadius: 3
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                    <Payment sx={{ fontSize: 40, mb: 1 }} />
                    <Typography gutterBottom variant="h5" component="h2">
                      Nóminas Procesadas
                    </Typography>
                    <Typography variant="h3" component="p">
                      {summaryData.totalPayrolls}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} sm={6} md={3}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 3, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    borderRadius: 3,
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  <PersonAdd color="primary" sx={{ fontSize: 50, mb: 2 }} />
                  <Typography variant="h6" align="center" gutterBottom>
                    Dar de Alta Empleados
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setEmployeeFormOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Acceder
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 3, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    borderRadius: 3
                  }}
                >
                  <People color="secondary" sx={{ fontSize: 50, mb: 2 }} />
                  <Typography variant="h6" align="center" gutterBottom>
                    Revisar Empleados
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    onClick={() => setEmployeeListOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Acceder
                  </Button>
                </Paper>
              </Grid>

              {/* Grid item de Registrar Faltas con icono bicolor personalizado */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 3, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    borderRadius: 3,
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  <BicolorEventIcon />
                  <Typography variant="h6" align="center" gutterBottom sx={{ mt: 2 }}>
                    Registrar Faltas
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ 
                      mt: 2,
                      background: 'linear-gradient(45deg, #d32f2f 30%, #2e7d32 90%)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #b71c1c 30%, #1b5e20 90%)'
                      }
                    }}
                    fullWidth
                    onClick={() => setAbsenceRegisterOpen(true)}
                  >
                    Acceder
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 3, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    borderRadius: 3
                  }}
                >
                  <Payment color="success" sx={{ fontSize: 50, mb: 2 }} />
                    <Typography variant="h6" align="center" gutterBottom>
                    Procesar Nómina
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={() => setPayrollProcessorOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Acceder
                  </Button>
                </Paper>
              </Grid>
            </Grid>

            {/* Sección de utilidad con solo el botón de Cerrar Sesión */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={handleLogout}
                sx={{ mb: 1 }}
              >
                Cerrar Sesión
              </Button>
              
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Empresa actual: ${empresaId}
              </Typography>
            </Box>
          </>
        )}
      </Container>
      
      <EmployeeForm 
        open={employeeFormOpen}
        onClose={() => setEmployeeFormOpen(false)}
        onSave={handleSaveEmployee}
        empresaId={empresaId}
      />
      
      <EmployeeList 
        open={employeeListOpen}
        onClose={() => setEmployeeListOpen(false)}
        employees={employees}
        onEdit={handleEditEmployee}
        onDelete={handleDeleteEmployee}
        onRefresh={fetchEmployees}
      />
      
      <PayrollProcessor 
        open={payrollProcessorOpen}
        onClose={() => setPayrollProcessorOpen(false)}
        onProcess={handleProcessPayroll}
      />
      
      <AbsenceRegister 
        open={absenceRegisterOpen}
        onClose={() => setAbsenceRegisterOpen(false)}
        employees={employees}
        onRegister={handleRegisterAbsences}
      />

      <EmployeeEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingEmployee(null);
        }}
        employee={editingEmployee}
        onSave={handleEditEmployee}
      />
    </Box>
  );
};

export default Dashboard;